import type { ModalCloseReason } from "@/components/modal/modalCloseReason";
import { captureEvent } from "@/lib/analytics";
import type { MarketplaceKey } from "@/marketplaces/registry";

export type MarketplaceModalOpenTrigger = "desktop_cta" | "mobile_fab" | "onboarding";

/** Закрытие без выбора маркетплейса (отвал на шаге). */
export type MarketplaceModalAbandonReason = ModalCloseReason | "programmatic" | "navigation_away" | "unknown";

type ActiveSession = {
  id: string;
  openedAt: number;
  trigger: MarketplaceModalOpenTrigger;
  openIndex: number;
  hovered: Set<MarketplaceKey>;
  sheetExpanded: boolean;
  completed: boolean;
};

let activeSession: ActiveSession | null = null;
let browserSessionOpenCount = 0;
/** Время последних отвалов (для повторных открытий и rage quit). */
let recentAbandonAt: number[] = [];

const QUICK_DISMISS_MS = 2000;
const HESITATION_MS = 8000;
const RAGE_WINDOW_MS = 5 * 60 * 1000;
const REPEAT_ABANDON_WINDOW_MS = 2 * 60 * 1000;

function createSessionId(): string {
  return `mp_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

function pruneAbandonTimestamps(now = Date.now()): void {
  recentAbandonAt = recentAbandonAt.filter((t) => now - t < RAGE_WINDOW_MS);
}

function countRecentAbandons(windowMs: number, now = Date.now()): number {
  return recentAbandonAt.filter((t) => now - t < windowMs).length;
}

function buildRageSignals(session: ActiveSession, timeOpenMs: number, now = Date.now()) {
  const priorAbandons5m = countRecentAbandons(RAGE_WINDOW_MS, now);
  const priorAbandons2m = countRecentAbandons(REPEAT_ABANDON_WINDOW_MS, now);
  const isQuickDismiss = timeOpenMs < QUICK_DISMISS_MS;
  const isRepeatAbandon = session.openIndex >= 2;
  const hesitationWithoutExplore = timeOpenMs >= HESITATION_MS && session.hovered.size === 0;
  const exploredButLeft = session.hovered.size > 0;

  const isRageQuit =
    (isQuickDismiss && isRepeatAbandon) ||
    priorAbandons2m >= 2 ||
    (isQuickDismiss && priorAbandons5m >= 1);

  return {
    is_quick_dismiss: isQuickDismiss,
    is_repeat_abandon: isRepeatAbandon,
    is_rage_quit: isRageQuit,
    hesitation_without_explore: hesitationWithoutExplore,
    explored_but_left: exploredButLeft,
    prior_abandon_count_5m: priorAbandons5m,
    prior_abandon_count_2m: priorAbandons2m,
  };
}

export function marketplaceModalOpened(
  trigger: MarketplaceModalOpenTrigger,
  extra?: { content_type_filter?: string }
): void {
  browserSessionOpenCount += 1;
  const openIndex = browserSessionOpenCount;
  pruneAbandonTimestamps();

  activeSession = {
    id: createSessionId(),
    openedAt: performance.now(),
    trigger,
    openIndex,
    hovered: new Set(),
    sheetExpanded: false,
    completed: false,
  };

  captureEvent("[CardGenius] Открытие выбора маркетплейса", {
    modal_session_id: activeSession.id,
    open_trigger: trigger,
    open_index: openIndex,
    prior_abandon_count_5m: countRecentAbandons(RAGE_WINDOW_MS),
    ...(extra?.content_type_filter ? { content_type_filter: extra.content_type_filter } : {}),
  });
}

export function marketplaceModalOptionHovered(marketplace: MarketplaceKey): void {
  const session = activeSession;
  if (!session || session.completed) return;
  if (session.hovered.has(marketplace)) return;

  session.hovered.add(marketplace);
  captureEvent("[CardGenius] Наведение на маркетплейс в модалке", {
    modal_session_id: session.id,
    marketplace,
    hover_index: session.hovered.size,
  });
}

export function marketplaceModalSheetGesture(action: "expand" | "collapse"): void {
  const session = activeSession;
  if (!session || session.completed) return;

  if (action === "expand") session.sheetExpanded = true;

  captureEvent("[CardGenius] Жест в модалке выбора маркетплейса", {
    modal_session_id: session.id,
    gesture: action === "expand" ? "sheet_expand" : "sheet_collapse",
    sheet_was_expanded: session.sheetExpanded,
  });
}

export function marketplaceModalSelected(marketplace: MarketplaceKey): void {
  const session = activeSession;
  if (!session) {
    captureEvent("[CardGenius] Выбор маркетплейса", { marketplace });
    return;
  }

  session.completed = true;
  const timeToSelectMs = Math.round(performance.now() - session.openedAt);

  captureEvent("[CardGenius] Выбор маркетплейса", {
    marketplace,
    modal_session_id: session.id,
    time_to_select_ms: timeToSelectMs,
    marketplaces_hovered: [...session.hovered],
    marketplaces_hovered_count: session.hovered.size,
    open_trigger: session.trigger,
    open_index: session.openIndex,
    sheet_was_expanded: session.sheetExpanded,
  });

  activeSession = null;
}

export function marketplaceModalAbandoned(reason: MarketplaceModalAbandonReason): void {
  const session = activeSession;
  if (!session || session.completed) return;

  const timeOpenMs = Math.round(performance.now() - session.openedAt);
  const rage = buildRageSignals(session, timeOpenMs);

  recentAbandonAt.push(Date.now());
  pruneAbandonTimestamps();

  captureEvent("[CardGenius] Закрытие выбора маркетплейса", {
    modal_session_id: session.id,
    close_reason: reason,
    time_open_ms: timeOpenMs,
    open_trigger: session.trigger,
    open_index: session.openIndex,
    marketplaces_hovered: [...session.hovered],
    marketplaces_hovered_count: session.hovered.size,
    sheet_was_expanded: session.sheetExpanded,
    is_mobile_viewport: typeof window !== "undefined" && window.innerWidth < 640,
    ...rage,
  });

  activeSession = null;
}

/** Закрытие вкладки / уход со страницы при открытой модалке. */
export function marketplaceModalAbandonOnNavigationAway(): void {
  if (!activeSession || activeSession.completed) return;
  marketplaceModalAbandoned("navigation_away");
}

export function isMarketplaceModalAnalyticsActive(): boolean {
  return activeSession != null && !activeSession.completed;
}
